import { ArrowRight, Clock3, KeyRound, Map, Shield, Trash2, Users } from "lucide-react";
import { FormEvent, useState } from "react";

import type { StoredCampaign } from "../lib/campaignStorage";

interface StartScreenProps {
  busy: boolean;
  error: Error | null;
  onStart: (name: string) => Promise<unknown>;
  saves?: StoredCampaign[];
  onOpen?: (id: string) => void;
  onRemove?: (id: string) => void;
  onRecover?: (key: string) => Promise<unknown>;
  onRecoveryKey?: (id: string) => string | null;
  onRotateRecoveryKey?: (id: string) => Promise<string | null>;
}
const promises = [
  { icon: Clock3, title: "The world keeps moving", text: "Work, travel and danger continue in real time." },
  { icon: Users, title: "Nobody is replaceable", text: "Skills grow. Bonds deepen. Death is permanent." },
  { icon: Map, title: "The surface remembers", text: "Locations change, supplies vanish and rumours go stale." },
  { icon: Shield, title: "Preparation is survival", text: "Build the fallback plan before the dead find the door." },
];

export function StartScreen({
  busy,
  error,
  onStart,
  saves = [],
  onOpen,
  onRemove,
  onRecover,
  onRecoveryKey,
  onRotateRecoveryKey,
}: StartScreenProps) {
  const [name, setName] = useState("The Last Light");
  const [recoveryInput, setRecoveryInput] = useState("");
  const [revealedRecovery, setRevealedRecovery] = useState<{ id: string; key: string } | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    await onStart(name);
  }

  async function recover(event: FormEvent) {
    event.preventDefault();
    if (!onRecover) return;
    await onRecover(recoveryInput);
  }

  return (
    <main className="start-screen">
      <div className="start-atmosphere" aria-hidden="true">
        <div className="distant-city" />
        <div className="start-earth" />
        <div className="start-cutaway">
          <div className="cutaway-shaft"><i /><i /><i /></div>
          <div className="cutaway-room room-a"><span /></div>
          <div className="cutaway-room room-b"><span /><span /></div>
          <div className="cutaway-room room-c"><span /></div>
          <div className="cutaway-tunnel" />
        </div>
      </div>

      <section className="start-content">
        <div className="brand-lockup">
          <span className="eyebrow">A persistent survival story</span>
          <h1>BUNKER<span>FALL</span></h1>
          <p className="start-tagline">Build below. Scavenge above. Keep the last light alive.</p>
        </div>

        <div className="start-promise-grid">
          {promises.map(({ icon: Icon, title, text }) => (
            <article key={title}>
              <Icon size={18} aria-hidden="true" />
              <div><h2>{title}</h2><p>{text}</p></div>
            </article>
          ))}
        </div>

        {saves.length > 0 && (
          <section className="saved-campaigns" aria-labelledby="saved-campaigns-title">
            <header>
              <div>
                <span className="eyebrow">Cloud-backed communities</span>
                <h2 id="saved-campaigns-title">Continue a community</h2>
              </div>
              <small>{saves.length} on this device</small>
            </header>
            <div className="saved-campaign-list">
              {saves.map((save) => (
                <article key={save.id}>
                  <button
                    className="saved-campaign-open"
                    disabled={busy}
                    onClick={() => onOpen?.(save.id)}
                  >
                    <Shield aria-hidden="true" />
                    <span>
                      <strong>{save.name}</strong>
                      <small>Last opened {new Date(save.lastOpenedAt).toLocaleDateString()}</small>
                    </span>
                    <ArrowRight aria-hidden="true" />
                  </button>
                  {save.accessKey && (
                    <button
                      className="saved-campaign-key"
                      aria-label={`Show recovery key for ${save.name}`}
                      onClick={() => {
                        const key = onRecoveryKey?.(save.id);
                        setRevealedRecovery(key ? { id: save.id, key } : null);
                      }}
                    >
                      <KeyRound aria-hidden="true" />
                    </button>
                  )}
                  <button
                    className="saved-campaign-remove"
                    aria-label={`Remove ${save.name} from this device`}
                    onClick={() => {
                      if (window.confirm(`Remove ${save.name} from this device? Keep its recovery key first if this is an anonymous save.`)) {
                        onRemove?.(save.id);
                      }
                    }}
                  >
                    <Trash2 aria-hidden="true" />
                  </button>
                </article>
              ))}
            </div>
            {revealedRecovery && (
              <div className="recovery-key-panel">
                <strong>Recovery key</strong>
                <output aria-live="polite">{revealedRecovery.key}</output>
                <small>Treat this like a password. It grants access to the save. Replacing it immediately revokes every previous copy.</small>
                <div className="recovery-key-actions">
                  <button onClick={() => void navigator.clipboard?.writeText(revealedRecovery.key)}>
                    Copy recovery key
                  </button>
                  {onRotateRecoveryKey && (
                    <button
                      className="danger-outline-button"
                      disabled={busy}
                      onClick={async () => {
                        if (!window.confirm("Replace this recovery key? Every previous copy will stop working immediately.")) return;
                        const key = await onRotateRecoveryKey(revealedRecovery.id);
                        if (key) setRevealedRecovery({ id: revealedRecovery.id, key });
                      }}
                    >
                      Replace recovery key
                    </button>
                  )}
                </div>
              </div>
            )}
          </section>
        )}

        <form className="start-form" onSubmit={submit}>
          <label htmlFor="community-name">Name your community</label>
          <div className="start-input-row">
            <input
              id="community-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              maxLength={80}
              autoComplete="off"
              disabled={busy}
            />
            <button className="primary-button" disabled={busy || !name.trim()}>
              {busy ? "Finding shelter…" : "Enter the bunker"}
              {!busy && <ArrowRight size={18} aria-hidden="true" />}
            </button>
          </div>
          {error && <p className="form-error" role="alert">{error.message}</p>}
          <p className="start-note">Your campaign is saved continuously on the server and remembered by this device.</p>
        </form>

        {onRecover && (
          <form className="recovery-import" onSubmit={recover}>
            <label htmlFor="recovery-key">Open with a recovery key</label>
            <div>
              <input
                id="recovery-key"
                value={recoveryInput}
                onChange={(event) => setRecoveryInput(event.target.value)}
                placeholder="BUNKERFALL1:campaign-id:capability"
                autoComplete="off"
                spellCheck={false}
                disabled={busy}
              />
              <button className="secondary-button" disabled={busy || !recoveryInput.trim()}>
                Validate and open
              </button>
            </div>
            <small>The key is sent only to the Bunkerfall server for validation.</small>
          </form>
        )}
      </section>

      <footer className="start-footer">
        <span>Somewhere in Britain</span><span>18:47 · Rain approaching</span>
      </footer>
    </main>
  );
}
